import {
  AdminAuditLogFilters,
  AdminBillingFilters,
  AdminBillingPlanCatalog,
  AdminBillingPlanInput,
  AdminAuditLogListPayload,
  AdminBillingOverview,
  AdminBillingProviderAccount,
  AdminClubDetail,
  AdminClubListItem,
  AdminConfigOverview,
  AdminGlobalConfigHistory,
  AdminGlobalConfigPatchInput,
  AdminFeatureFlagHistory,
  AdminFeatureFlagOverrideInput,
  AdminFeatureFlagListPayload,
  AdminIncidentDetail,
  AdminIncidentListPayload,
  AdminOverview,
  AdminOwnerDetail,
  AdminOwnerListItem,
  AdminSecurityAlertFilters,
  AdminSecurityAlertEvidence,
  AdminSecurityAlertListPayload,
  AdminSecurityAlertWorkflow,
  AdminSecurityClubHistory,
  AdminSecurityClubRiskPayload,
  AdminSecurityLoginProtectionOverview,
  AdminSecurityOverview,
  AdminSecurityRuleEngine,
  AdminSecurityRuleConfigHistory,
  AdminSecurityRuleConfigOverrideInput,
  AdminSecurityPlayerHistory,
  AdminSecurityPlayerRisk,
  AdminSecurityRelationships,
  AdminSecurityTableHistory,
  AdminSecurityTableRiskPayload,
  AdminRealtimeOverview,
  AdminSystemHttpMetrics,
  AdminSystemJobs,
  AdminSystemClubCreationErrors,
  AdminSystemWebhooks,
  AdminPlayerBehavior,
  AdminPlayerDetail,
  AdminPlayerFinancialTrail,
  AdminPlayerListItem,
  AdminPlayerOwnerAccess,
  AdminPlayerOwnerAccessInput,
  AdminAnnouncement,
  AdminAnnouncementInput,
} from '../types';
import { API_BASE_URL } from '../config/env';
import { apiRequest } from './apiClient';
import { getAuthToken } from './authStorage';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

const encodeSearch = (value?: string): string => encodeURIComponent((value || '').trim());

export const adminService = {
  async listAnnouncements(): Promise<AdminAnnouncement[]> {
    const payload = await apiRequest<ApiEnvelope<AdminAnnouncement[]>>('/admin/announcements');
    return payload.data;
  },

  async createAnnouncement(input: AdminAnnouncementInput): Promise<AdminAnnouncement> {
    const payload = await apiRequest<ApiEnvelope<AdminAnnouncement>>('/admin/announcements', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return payload.data;
  },

  async updateAnnouncement(id: string, input: Partial<AdminAnnouncementInput> & { reason: string }): Promise<AdminAnnouncement> {
    const payload = await apiRequest<ApiEnvelope<AdminAnnouncement>>(`/admin/announcements/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return payload.data;
  },

  async uploadAnnouncementImage(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('image', file);
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/admin/announcements/image`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: formData,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Erro ao enviar imagem');
    return String(payload.data?.url || '');
  },

  async getOverview(): Promise<AdminOverview> {
    const payload = await apiRequest<ApiEnvelope<AdminOverview>>('/admin/overview');
    return payload.data;
  },

  async getBillingOverview(filters: AdminBillingFilters = {}): Promise<AdminBillingOverview> {
    const params = new URLSearchParams();

    if (filters.ownerSearch) params.set('ownerSearch', filters.ownerSearch.trim());
    if (filters.clubSearch) params.set('clubSearch', filters.clubSearch.trim());
    if (filters.subscriptionStatus) params.set('subscriptionStatus', filters.subscriptionStatus.trim());
    if (filters.paymentStatus) params.set('paymentStatus', filters.paymentStatus.trim());
    if (filters.limit) params.set('limit', String(filters.limit));

    const queryString = params.toString();
    const payload = await apiRequest<ApiEnvelope<AdminBillingOverview>>(
      `/admin/billing/overview${queryString ? `?${queryString}` : ''}`
    );
    return payload.data;
  },

  async getBillingPlans(): Promise<AdminBillingPlanCatalog> {
    const payload = await apiRequest<ApiEnvelope<AdminBillingPlanCatalog>>('/admin/billing/plans');
    return payload.data;
  },

  async createBillingPlan(input: AdminBillingPlanInput): Promise<AdminBillingPlanCatalog> {
    const payload = await apiRequest<ApiEnvelope<AdminBillingPlanCatalog>>('/admin/billing/plans', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return payload.data;
  },

  async updateBillingPlan(planCode: string, input: AdminBillingPlanInput): Promise<AdminBillingPlanCatalog> {
    const payload = await apiRequest<ApiEnvelope<AdminBillingPlanCatalog>>(
      `/admin/billing/plans/${encodeURIComponent(planCode)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(input),
      }
    );
    return payload.data;
  },

  async getBillingProviderAccount(): Promise<AdminBillingProviderAccount> {
    const payload = await apiRequest<ApiEnvelope<AdminBillingProviderAccount>>('/admin/billing/provider-account');
    return payload.data;
  },

  async validateBillingProviderAccount(input: {
    environment: 'SANDBOX' | 'PRODUCTION';
    apiKey: string;
    webhookUrl?: string;
  }): Promise<any> {
    const payload = await apiRequest<ApiEnvelope<any>>('/admin/billing/provider-account/validate', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return payload.data;
  },

  async upsertBillingProviderAccount(input: {
    environment: 'SANDBOX' | 'PRODUCTION';
    apiKey?: string;
    webhookToken?: string;
    webhookUrl?: string;
    status?: 'PENDING_SETUP' | 'ACTIVE' | 'RESTRICTED' | 'DISABLED';
    validateBeforeSave?: boolean;
  }): Promise<AdminBillingProviderAccount> {
    const payload = await apiRequest<ApiEnvelope<AdminBillingProviderAccount>>('/admin/billing/provider-account', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return payload.data;
  },

  async getConfigOverview(): Promise<AdminConfigOverview> {
    const payload = await apiRequest<ApiEnvelope<AdminConfigOverview>>('/admin/config');
    return payload.data;
  },

  async getGlobalConfigHistory(groupKey: string): Promise<AdminGlobalConfigHistory> {
    const payload = await apiRequest<ApiEnvelope<AdminGlobalConfigHistory>>(
      `/admin/config/${encodeURIComponent(groupKey)}/history`
    );
    return payload.data;
  },

  async patchGlobalConfig(input: AdminGlobalConfigPatchInput): Promise<AdminGlobalConfigHistory> {
    const payload = await apiRequest<ApiEnvelope<AdminGlobalConfigHistory>>('/admin/config', {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return payload.data;
  },

  async listFeatureFlags(): Promise<AdminFeatureFlagListPayload> {
    const payload = await apiRequest<ApiEnvelope<AdminFeatureFlagListPayload>>('/admin/feature-flags');
    return payload.data;
  },

  async getFeatureFlagHistory(flagKey: string): Promise<AdminFeatureFlagHistory> {
    const payload = await apiRequest<ApiEnvelope<AdminFeatureFlagHistory>>(
      `/admin/feature-flags/${encodeURIComponent(flagKey)}/history`
    );
    return payload.data;
  },

  async overrideFeatureFlag(
    flagKey: string,
    input: AdminFeatureFlagOverrideInput
  ): Promise<AdminFeatureFlagHistory> {
    const payload = await apiRequest<ApiEnvelope<AdminFeatureFlagHistory>>(
      `/admin/feature-flags/${encodeURIComponent(flagKey)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(input),
      }
    );
    return payload.data;
  },

  async listIncidents(): Promise<AdminIncidentListPayload> {
    const payload = await apiRequest<ApiEnvelope<AdminIncidentListPayload>>('/admin/incidents');
    return payload.data;
  },

  async getIncidentDetail(incidentId: string): Promise<AdminIncidentDetail> {
    const payload = await apiRequest<ApiEnvelope<AdminIncidentDetail>>(`/admin/incidents/${incidentId}`);
    return payload.data;
  },

  async assignIncident(
    incidentId: string,
    input: { reason: string; assigneeUserId?: string }
  ): Promise<AdminIncidentDetail> {
    const payload = await apiRequest<ApiEnvelope<AdminIncidentDetail>>(`/admin/incidents/${incidentId}/assign`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return payload.data;
  },

  async acknowledgeIncident(
    incidentId: string,
    input: { reason: string }
  ): Promise<AdminIncidentDetail> {
    const payload = await apiRequest<ApiEnvelope<AdminIncidentDetail>>(
      `/admin/incidents/${incidentId}/acknowledge`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
    return payload.data;
  },

  async resolveIncident(
    incidentId: string,
    input: { reason: string }
  ): Promise<AdminIncidentDetail> {
    const payload = await apiRequest<ApiEnvelope<AdminIncidentDetail>>(`/admin/incidents/${incidentId}/resolve`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return payload.data;
  },

  async getSecurityOverview(): Promise<AdminSecurityOverview> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityOverview>>('/admin/security/overview');
    return payload.data;
  },

  async getSecurityLoginProtectionOverview(): Promise<AdminSecurityLoginProtectionOverview> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityLoginProtectionOverview>>(
      '/admin/security/login-protection'
    );
    return payload.data;
  },

  async getSecurityRuleEngine(): Promise<AdminSecurityRuleEngine> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityRuleEngine>>('/admin/security/rule-engine');
    return payload.data;
  },

  async getSecurityRuleConfigHistory(ruleCode: string): Promise<AdminSecurityRuleConfigHistory> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityRuleConfigHistory>>(
      `/admin/security/rules/${encodeURIComponent(ruleCode)}/history`
    );
    return payload.data;
  },

  async overrideSecurityRuleConfig(
    ruleCode: string,
    input: AdminSecurityRuleConfigOverrideInput
  ): Promise<AdminSecurityRuleConfigHistory> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityRuleConfigHistory>>(
      `/admin/security/rules/${encodeURIComponent(ruleCode)}/override`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
    return payload.data;
  },

  async getSecurityTableRisk(limit = 8): Promise<AdminSecurityTableRiskPayload> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityTableRiskPayload>>(
      `/admin/security/tables?limit=${limit}`
    );
    return payload.data;
  },

  async getSecurityClubRisk(limit = 8): Promise<AdminSecurityClubRiskPayload> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityClubRiskPayload>>(
      `/admin/security/clubs?limit=${limit}`
    );
    return payload.data;
  },

  async listSecurityAlerts(
    filters: AdminSecurityAlertFilters = {}
  ): Promise<AdminSecurityAlertListPayload> {
    const params = new URLSearchParams();

    if (filters.search) params.set('search', filters.search.trim());
    if (filters.severity) params.set('severity', filters.severity.trim());
    if (filters.status) params.set('status', filters.status.trim());
    if (filters.targetType) params.set('targetType', filters.targetType.trim());
    if (filters.limit) params.set('limit', String(filters.limit));

    const queryString = params.toString();
    const payload = await apiRequest<ApiEnvelope<AdminSecurityAlertListPayload>>(
      `/admin/security/alerts${queryString ? `?${queryString}` : ''}`
    );
    return payload.data;
  },

  async getSecurityPlayerRisk(userId: string): Promise<AdminSecurityPlayerRisk> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityPlayerRisk>>(
      `/admin/security/players/${userId}/risk`
    );
    return payload.data;
  },

  async getSecurityPlayerHistory(userId: string): Promise<AdminSecurityPlayerHistory> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityPlayerHistory>>(
      `/admin/security/players/${userId}/history`
    );
    return payload.data;
  },

  async getSecurityPlayerRelationships(userId: string): Promise<AdminSecurityRelationships> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityRelationships>>(
      `/admin/security/players/${userId}/relationships`
    );
    return payload.data;
  },

  async getSecurityTableHistory(tableId: string): Promise<AdminSecurityTableHistory> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityTableHistory>>(
      `/admin/security/tables/${tableId}/history`
    );
    return payload.data;
  },

  async getSecurityClubHistory(clubId: string): Promise<AdminSecurityClubHistory> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityClubHistory>>(
      `/admin/security/clubs/${clubId}/history`
    );
    return payload.data;
  },

  async getSecurityAlertWorkflow(alertId: string): Promise<AdminSecurityAlertWorkflow> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityAlertWorkflow>>(
      `/admin/security/alerts/${alertId}/workflow`
    );
    return payload.data;
  },

  async getSecurityAlertEvidence(alertId: string): Promise<AdminSecurityAlertEvidence> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityAlertEvidence>>(
      `/admin/security/alerts/${alertId}/evidence`
    );
    return payload.data;
  },

  async assignSecurityAlert(
    alertId: string,
    input: {
      reason: string;
      assigneeUserId?: string;
    }
  ): Promise<AdminSecurityAlertWorkflow> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityAlertWorkflow>>(
      `/admin/security/alerts/${alertId}/assign`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
    return payload.data;
  },

  async resolveSecurityAlert(
    alertId: string,
    input: {
      reason: string;
      resolution: 'RESOLVED' | 'DISMISSED';
    }
  ): Promise<AdminSecurityAlertWorkflow> {
    const payload = await apiRequest<ApiEnvelope<AdminSecurityAlertWorkflow>>(
      `/admin/security/alerts/${alertId}/resolve`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
    return payload.data;
  },

  async getRealtimeOverview(): Promise<AdminRealtimeOverview> {
    const payload = await apiRequest<ApiEnvelope<AdminRealtimeOverview>>('/admin/realtime');
    return payload.data;
  },

  async getSystemHttpMetrics(): Promise<AdminSystemHttpMetrics> {
    const payload = await apiRequest<ApiEnvelope<AdminSystemHttpMetrics>>('/admin/system/http-metrics');
    return payload.data;
  },

  async listSystemJobs(): Promise<AdminSystemJobs> {
    const payload = await apiRequest<ApiEnvelope<AdminSystemJobs>>('/admin/system/jobs');
    return payload.data;
  },

  async listSystemWebhooks(): Promise<AdminSystemWebhooks> {
    const payload = await apiRequest<ApiEnvelope<AdminSystemWebhooks>>('/admin/system/webhooks');
    return payload.data;
  },

  async listSystemClubCreationErrors(): Promise<AdminSystemClubCreationErrors> {
    const payload = await apiRequest<ApiEnvelope<AdminSystemClubCreationErrors>>(
      '/admin/system/club-creation-errors'
    );
    return payload.data;
  },

  async listClubs(search = ''): Promise<AdminClubListItem[]> {
    const payload = await apiRequest<ApiEnvelope<AdminClubListItem[]>>(
      `/admin/clubs?limit=40&search=${encodeSearch(search)}`
    );
    return payload.data;
  },

  async getClubDetail(clubId: string): Promise<AdminClubDetail> {
    const payload = await apiRequest<ApiEnvelope<AdminClubDetail>>(`/admin/clubs/${clubId}`);
    return payload.data;
  },

  async listOwners(search = ''): Promise<AdminOwnerListItem[]> {
    const payload = await apiRequest<ApiEnvelope<AdminOwnerListItem[]>>(
      `/admin/owners?limit=40&search=${encodeSearch(search)}`
    );
    return payload.data;
  },

  async getOwnerDetail(ownerUserId: string): Promise<AdminOwnerDetail> {
    const payload = await apiRequest<ApiEnvelope<AdminOwnerDetail>>(`/admin/owners/${ownerUserId}`);
    return payload.data;
  },

  async listPlayers(search = ''): Promise<AdminPlayerListItem[]> {
    const payload = await apiRequest<ApiEnvelope<AdminPlayerListItem[]>>(
      `/admin/players?limit=40&search=${encodeSearch(search)}`
    );
    return payload.data;
  },

  async getPlayerDetail(userId: string): Promise<AdminPlayerDetail> {
    const payload = await apiRequest<ApiEnvelope<AdminPlayerDetail>>(`/admin/players/${userId}`);
    return payload.data;
  },

  async getPlayerFinancialTrail(userId: string): Promise<AdminPlayerFinancialTrail> {
    const payload = await apiRequest<ApiEnvelope<AdminPlayerFinancialTrail>>(
      `/admin/players/${userId}/financial-trail`
    );
    return payload.data;
  },

  async getPlayerBehavior(userId: string): Promise<AdminPlayerBehavior> {
    const payload = await apiRequest<ApiEnvelope<AdminPlayerBehavior>>(
      `/admin/players/${userId}/behavior`
    );
    return payload.data;
  },

  async getPlayerOwnerAccess(userId: string): Promise<AdminPlayerOwnerAccess> {
    const payload = await apiRequest<ApiEnvelope<AdminPlayerOwnerAccess>>(
      `/admin/players/${userId}/owner-access`
    );
    return payload.data;
  },

  async upsertPlayerOwnerAccess(
    userId: string,
    input: AdminPlayerOwnerAccessInput
  ): Promise<AdminPlayerOwnerAccess> {
    const payload = await apiRequest<ApiEnvelope<AdminPlayerOwnerAccess>>(
      `/admin/players/${userId}/owner-access`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );
    return payload.data;
  },

  async listAuditLogs(filters: AdminAuditLogFilters = {}): Promise<AdminAuditLogListPayload> {
    const params = new URLSearchParams();

    if (filters.adminSearch) params.set('adminSearch', filters.adminSearch.trim());
    if (filters.actionType) params.set('actionType', filters.actionType.trim());
    if (filters.targetSearch) params.set('targetSearch', filters.targetSearch.trim());
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    params.set('limit', String(filters.limit || 50));

    const queryString = params.toString();
    const payload = await apiRequest<ApiEnvelope<AdminAuditLogListPayload>>(
      `/admin/audit-logs${queryString ? `?${queryString}` : ''}`
    );

    return payload.data;
  },
};
